/**
 * Nicknames and the handle that names a player: `Nickname#0042`. The rules here are the ones the
 * database holds in its checks (3 to 16 characters, no space at either end, letters, digits and
 * `_ . - space`), plus what only code can judge: no run of spaces, at least one letter, nothing
 * from the lists of words a name may not carry. The lists are a first filter, not moderation (the
 * report queue of milestone 7 is).
 */

export const NICKNAME_MIN = 3;
export const NICKNAME_MAX = 16;

/** Same set as the `profiles_nickname_shape` check of the first migration. */
const ALLOWED = /^[A-Za-z0-9À-ÖØ-öø-ÿ_. -]+$/;
const LETTER = /[A-Za-zÀ-ÖØ-öø-ÿ]/;

export type NicknameProblem =
  'tooShort' | 'tooLong' | 'characters' | 'noLetter' | 'blocked' | 'reserved';

export type NicknameCheck =
  { ok: true; nickname: string } | { ok: false; problem: NicknameProblem };

/**
 * What a name must not carry once it is boiled down (see `skeleton`). Italian and English.
 * A list is a first line, not moderation, and every word in it costs some honest names: so there are
 * two kinds. Distinctive stems are refused anywhere in the name. Stems that sit inside innocent
 * words (the Scunthorpe problem) are refused only at the start or the end of the name, where a
 * disguised insult is, and not in the middle of "Scunthorpe". It still refuses "Shitake" and
 * "Nigeria"; those players can pick another name.
 */
const BLOCKED_ANYWHERE = [
  'cazzo',
  'merda',
  'stronz',
  'puttan',
  'vaffanc',
  'coglion',
  'frocio',
  'fuck',
  'whore',
  'hitler',
];
const BLOCKED_AT_EDGES = ['shit', 'cunt', 'bitch', 'nigger', 'nigga', 'faggot', 'pussy'];

/** Names that only the studio may carry: the whole boiled-down name must differ from these. */
const RESERVED = [
  'admin',
  'administrator',
  'moderator',
  'mod',
  'staff',
  'support',
  'system',
  'server',
  'bot',
  'ilcolpo',
  'colpo',
  'anonymous',
  'anonimo',
  'null',
  'undefined',
];

/** Letters that stand in for others in a disguised word. */
const LOOKALIKES: Record<string, string> = {
  '0': 'o',
  '1': 'i',
  '3': 'e',
  '4': 'a',
  '5': 's',
  '7': 't',
  '@': 'a',
  $: 's',
};

/**
 * A name boiled down for comparison: lower case, no accents, look-alike digits turned to the
 * letters they pass for, separators dropped, runs of one letter cut to one.
 */
export function skeleton(name: string): string {
  const plain = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[0134578@$]/g, (c) => LOOKALIKES[c] ?? c)
    .replace(/[^a-z]/g, '');
  return plain.replace(/(.)\1+/g, '$1');
}

/** Trims, joins runs of spaces and puts accents in one canonical form. */
export function cleanNickname(raw: string): string {
  return raw.normalize('NFC').trim().replace(/ {2,}/g, ' ');
}

export function checkNickname(raw: string): NicknameCheck {
  const nickname = cleanNickname(raw);
  if (nickname.length < NICKNAME_MIN) return { ok: false, problem: 'tooShort' };
  if (nickname.length > NICKNAME_MAX) return { ok: false, problem: 'tooLong' };
  if (!ALLOWED.test(nickname)) return { ok: false, problem: 'characters' };
  if (!LETTER.test(nickname)) return { ok: false, problem: 'noLetter' };
  const boiled = skeleton(nickname);
  if (RESERVED.some((r) => skeleton(r) === boiled)) return { ok: false, problem: 'reserved' };
  // Stems are boiled down too (`cazzo` is `cazo` once its repeats are cut).
  const anywhere = BLOCKED_ANYWHERE.some((stem) => boiled.includes(skeleton(stem)));
  const atEdges = BLOCKED_AT_EDGES.some((stem) => {
    const k = skeleton(stem);
    return boiled.startsWith(k) || boiled.endsWith(k);
  });
  if (anywhere || atEdges) return { ok: false, problem: 'blocked' };
  return { ok: true, nickname };
}

/** The four digits of a tag: `42` → `0042`. */
export function formatTag(tag: number): string {
  return String(Math.min(9999, Math.max(0, Math.trunc(tag)))).padStart(4, '0');
}

/** `Nickname#0042`. */
export function handleOf(nickname: string, tag: number): string {
  return `${nickname}#${formatTag(tag)}`;
}

/** Reads `Nickname#0042` back: the last `#` splits the name from four digits. */
export function parseHandle(text: string): { nickname: string; tag: number } | null {
  const m = /^(.+)#(\d{4})$/.exec(text.trim());
  if (!m) return null;
  const checked = checkNickname(m[1] ?? '');
  return checked.ok ? { nickname: checked.nickname, tag: Number(m[2]) } : null;
}

/**
 * Chat: words that the list above refuses are covered with asterisks, word by word (a word is a run of
 * letters and the look-alike digits), so that one bad word does not cost the whole message. The
 * same limits as for names: a first line, not moderation.
 */
export function maskProfanity(text: string): { text: string; masked: boolean } {
  let masked = false;
  const out = text.replace(/[A-Za-zÀ-ÖØ-öø-ÿ0134578@$]+/g, (word) => {
    const boiled = skeleton(word);
    const bad =
      BLOCKED_ANYWHERE.some((stem) => boiled.includes(skeleton(stem))) ||
      BLOCKED_AT_EDGES.some((stem) => {
        const k = skeleton(stem);
        return boiled.startsWith(k) || boiled.endsWith(k);
      });
    if (!bad) return word;
    masked = true;
    return '*'.repeat(Math.min(word.length, 8));
  });
  return { text: out, masked };
}
